// Scene layers: parts a visitor adds to the music from things in the scene (issue #38). A scene's spot of a layer kind
// (site/js/ui/spots.js: the turntable, the desk lamp and the painting in Paris) is a switch: tapped, its part joins the
// track on the next bar line, and tapped again it stops on the next one (player.setLayer). A bar line, not a beat: the
// parts are bar-long figures, like the band, which also changes on a bar line (player.setVibe), so one never comes in
// half-way through its figure; the wait is a bar at most (1.4 s at the fastest, 4.4 s at the slowest), and the page
// shows the switch as waiting meanwhile. Three parts, each made from the track itself, so it fits whatever plays:
//   shaker    a soft shaker on the track's 8th notes (its ghost 16ths swung like the track's hats, its "and"s as late
//             as the style's); a track without a beat of its own (Ambient at chill) gets none: it rests there
//   pad       a warm pad holding the notes the keys are sounding, chord by chord (never a note the chord hasn't got)
//   twinkle   a broken chord in a soft voice of the track's style (jam.js's voices), each note one of the eight that
//             fit the chord sounding when it lands (jam.js jamNotes)
// They go into the music bus, under the band (LAYER, measured below), so the record's tone, tape and wobble, the music
// slider and mute reach them like the band. Nothing here touches the engine's plan: the parts only read what sounds
// (engine.harmony), and with no layer on not one node of this file exists.
//   barAfter(start, bpm, at) · nextBar(start, bpm, at) · layerState() · askLayer(L, want, bar) · soundsAt(L, step)
//   layerFits(id, plan) · shakerBar(plan, bar) · twinkleBar(plan, bar) · padNotes(tones, root)   (pure: tests/layers.test.mjs)
//   createLayers(ac, mixer, { registry, lite }) → { set(id, on, engine, at), advance(until, engine, now, lag), clear(at), states(engine), stats(), dispose() }
import { hash } from '../engine/core.js';
import { mtof, noise } from './dsp.js';
import { swing } from './rhythm.js';
import { eighthLate, jamNotes, jamLow, createJam } from './jam.js';

export const LAYER_IDS = ['shaker', 'pad', 'twinkle'];
export const LAYER_AHEAD = 0.05;      // s: nothing is scheduled, and no switch flips, nearer than this
// Levels: `level` is each part's bus into the music bus (the track buses' own is 0.5), `send` its share to the reverb.
// Set from offline renders of each part alone through the mixer at the default music slider, beside the band, in all
// 18 styles (8 bars from the first A, loudest 400 ms; the band alone is at −14 to −18 dBFS there): the pad sits 10 to
// 14 dB under the full band, the twinkle 9 to 17 (the plucked voices lowest: their picks peak, their ring is soft),
// all three together 6 to 12 dB under it, and they raise the mix by 0.4 to 0.9 dB, its peak by 1.4 dB at most and
// never over −3.3 dBFS. On Chords only, against the keys alone: 5 to 10 dB under, 1.2 dB at most. A shaker is all
// transient, so it is set beside its own kind instead: its shakes peak near −29 dBFS, 5 to 9 dB over the band's own
// hi-hat and shaker (instruments.js createKit / createPerc, each alone on a track bus) and 24 dB under the band's
// peaks; set against the whole band's loudness it came out 24 dB over them.
export const LAYER = {
  shaker: { level: 0.5, send: 0.08, pan: -0.25 },
  pad: { level: 0.5, send: 0.3, pan: -0.1 },
  twinkle: { level: 0.26, send: 0.3, pan: 0.15 },
};
const SHAKE_GAIN = 0.3, PAD_GAIN = 0.06;   // a shake's peak per unit of its level; the pad's root (its other notes 85 % of it)
const mod12 = n => ((n % 12) + 12) % 12;
const tagOf = id => `layer:${id}`;

/* ---------------- when: the next bar line ---------------- */
/** The first bar line at or after `at` (ac seconds), as a bar of a track that started at `start` at `bpm`: under 0
 *  before the track, its bar count or more past its end (the grid runs on both ways). */
export const barAfter = (start, bpm, at) => Math.ceil((at - start) / (240 / bpm) - 1e-9) + 0;   // (+ 0: never −0)
/** That bar line's time: at or after `at`, less than a bar away. */
export const nextBar = (start, bpm, at) => start + barAfter(start, bpm, at) * (240 / bpm);

/* ---------------- a layer's switch ---------------- */
/** { want: what the visitor asked for, on: what sounds, bar: the bar of the track playing on whose line `on` becomes
 *  `want` (null: nothing waits, or it waits for a track to have a bar line to give), next: the first 16th step of the
 *  track not looked at yet } */
export const layerState = () => ({ want: false, on: false, bar: null, next: 0 });
/** Whether the layer sounds on 16th step `step` of the track playing. */
export const soundsAt = (L, step) => (L.bar !== null && step >= L.bar * 16 ? L.want : L.on);
/**
 * The visitor sets the switch to `want`; `bar` is the next bar line of the track playing (null: no track, or none left
 * in it). Returns 'same' (already so), 'back' (it was waiting to change and now stays as it sounds: nothing changes on
 * that bar line after all) or 'flip' (it changes on `bar`). After a 'back' or a 'flip', what was scheduled from that
 * bar line on is no longer right: L.next is moved back to it (createLayers stops those notes).
 */
export function askLayer(L, want, bar) {
  want = !!want;
  if (want === L.want) return 'same';
  L.want = want;
  const back = want === L.on, from = back ? L.bar : bar;
  L.bar = back ? null : bar;
  if (from !== null) L.next = Math.min(L.next, Math.max(0, from) * 16);
  return back ? 'back' : 'flip';
}

/* ---------------- what: each part's bar ---------------- */
/** Whether a part plays in a track at all. Only the shaker can't always: a track with no beat of its own in any
 *  section (Ambient at chill) would get a beat it was written without, so there the shaker rests. */
export const layerFits = (id, plan) => LAYER_IDS.includes(id) && !!plan
  && (id !== 'shaker' || plan.sections.some(s => s.layers.drums && s.layers.drums !== 'none'));

// The shaker's bar, a level per 16th step: the 8ths, the "and"s leaning; at upbeat, ghost 16ths between them. A track
// that keeps its beat to a few hits a bar (Ambient) gets the "and"s alone, and a fast one (from 100 bpm: house, liquid
// DnB) no ghosts, whose 16ths its own kit already fills.
const SHAKES = {
  thin: [0, 0, 0.5, 0, 0, 0, 0.44, 0, 0, 0, 0.5, 0, 0, 0, 0.44, 0],
  chill: [0.36, 0, 0.6, 0, 0.32, 0, 0.6, 0, 0.36, 0, 0.6, 0, 0.32, 0, 0.64, 0],
  balanced: [0.46, 0, 0.76, 0, 0.42, 0, 0.76, 0, 0.46, 0, 0.76, 0, 0.42, 0, 0.8, 0],
  upbeat: [0.48, 0.2, 0.8, 0.2, 0.44, 0.2, 0.8, 0.22, 0.48, 0.2, 0.8, 0.2, 0.44, 0.2, 0.84, 0.28],
};
const shakeOf = plan => (plan.vibe.style === 'ambient' ? 'thin' : plan.vibe.energy === 'upbeat' && plan.bpm >= 100 ? 'balanced'
  : SHAKES[plan.vibe.energy] ? plan.vibe.energy : 'balanced');
/** One bar of the shaker: [{ s: 16th step, dt: seconds after its place on the grid, vel, long: an "and" }], a pure
 *  function of the plan and the bar. dt: the track's swing on the odd 16ths, the style's late "and"s (dub), and a
 *  hand's few ms. */
export function shakerBar(plan, bar) {
  if (!layerFits('shaker', plan)) return [];
  const sd = 15 / plan.bpm, late = eighthLate(plan), out = [];
  SHAKES[shakeOf(plan)].forEach((v, s) => {
    if (!v) return;
    const dt = (s % 2 ? swing(s, sd, plan.swing) : s % 4 === 2 ? late : 0) + (hash(plan.seed, bar * 16 + s, 381) - 0.5) * 0.006;
    out.push({ s, dt: Math.max(0, dt), vel: v * (0.92 + 0.16 * hash(plan.seed, bar * 16 + s, 382)), long: s % 4 === 2 });
  });
  return out;
}

// The twinkle's figures, a key (0–7, low to high, of the eight notes that fit the chord) or a rest per slot. A slot is
// an 8th note; from 130 bpm (liquid DnB) a quarter, so a figure spans two bars there. Chill tracks draw from the
// sparser ones.
export const TWINKLES = [
  [0, null, 3, 4, null, 2, 4, null],
  [0, 3, null, 2, 4, null, 3, null],
  [0, 2, 4, 2, 5, 4, 2, null],
  [4, 2, 0, 2, 4, 5, 4, null],
];
/** One bar of the twinkle: [{ s, dt, i: the key }], a pure function of the plan and the bar; the note is picked when it
 *  lands, from the chord sounding then. Every fourth bar or so it leaves its last notes out, for breath. */
export function twinkleBar(plan, bar) {
  const unit = plan.bpm >= 130 ? 4 : 2, late = unit === 2 ? eighthLate(plan) : 0;
  const pool = plan.vibe.energy === 'chill' ? 2 : TWINKLES.length, fig = TWINKLES[Math.floor(hash(plan.seed, 383) * pool)];
  const breath = hash(plan.seed, bar, 384) < 0.25, out = [];
  for (let s = 0; s < 16; s += unit) {
    const k = ((bar * 16 + s) / unit) % 8, i = fig[k];
    if (i === null || (breath && k >= 5 && unit === 2)) continue;
    out.push({ s, dt: s % 4 === 2 ? late : 0, i });
  }
  return out;
}

/** The pad's notes (MIDI, low to high) for a chord sounding the pitch classes `tones` over the root `root` (the
 *  engine's harmonyAt gives both): the root between C3 and B3, its fifth where the chord has one, and up to two of
 *  its other tones (a third first, then a seventh) between C4 and B4. Only notes that are sounding. */
export function padNotes(tones, root) {
  const rel = [...new Set(tones.map(pc => mod12(pc - root)))], low = 48 + mod12(root - 48), notes = [low];
  if (rel.includes(7)) notes.push(low + 7);
  const want = [3, 4, 10, 11, 2, 9, 5, 6, 8, 1];
  const colours = want.filter(i => rel.includes(i)).slice(0, 2).map(i => 60 + mod12(root + i - 60));
  return [...new Set([...notes, ...colours])].sort((a, b) => a - b);
}

/* ---------------- how: the voices and the schedule ---------------- */
/**
 * createLayers(ac, mixer, { registry, lite }) → { set, advance, clear, states, stats, dispose }
 * `engine` is the player's engine, or anything with its { plan, startTime, trackNo, harmony(at) }.
 * set(id, on, engine, at): the switch, flipped at ac time `at` (default now): it takes effect on the track's next bar
 *   line. Returns askLayer's word.
 * advance(until, engine, now, lag): schedules every part that sounds up to `until`, after engine.advance(until) (the
 *   bars it reads are planned by then). Returns true when a switch took effect since the last call. `now`: the ac
 *   time it is (an offline render, which schedules everything before it starts, says where it would be); `lag`: the
 *   output's latency (s), so a switch is said to have taken effect when its bar line is heard.
 * clear(at): every switch off at once (the radio tuned in): what sounds is let go (τ 0.1 s).
 * states(engine) → { id: { want, on, fits, at: the ac time it changes at | null } }
 * Every note is a one-shot in the registry, tagged `layer:<id>`, but the pad's chord, held until the next one.
 */
export function createLayers(ac, mixer, { registry, lite = false }) {
  const S = Object.fromEntries(LAYER_IDS.map(id => [id, { ...layerState(), barNo: -1, events: [], last: false, held: null, key: null, ends: null }]));
  const buses = {}, played = Object.fromEntries(LAYER_IDS.map(id => [id, 0])), log = [];
  let trackNo = null, trackEnd = -Infinity, twinkle = null;

  /** a part's bus: → its level → its pan → the music bus, and its share of the reverb; built when the part first sounds */
  function busOf(id) {
    if (buses[id]) return buses[id];
    const g = ac.createGain(), p = ac.createStereoPanner(), send = ac.createGain(), { level, send: wet, pan } = LAYER[id];
    g.gain.value = level; p.pan.value = pan; send.gain.value = wet;
    g.connect(p); p.connect(mixer.musicIn); g.connect(send); send.connect(mixer.revIn);
    buses[id] = { in: g, v: registry.add({ src: [], all: [g, p, send], tag: `${tagOf(id)}:bus` }) };
    return buses[id];
  }
  const note = (id, step, when, extra) => { played[id]++; log.push({ id, step, when: +when.toFixed(4), ...extra }); if (log.length > 96) log.shift(); };

  /** one shake: noise through a band-pass, in softly (the beads take a moment) and out fast; an "and" a little longer */
  function shake(when, e) {
    const s = ac.createBufferSource(), bp = ac.createBiquadFilter(), hp = ac.createBiquadFilter(), g = ac.createGain();
    const atk = 0.009, tau = e.long ? 0.03 : 0.018, end = when + atk + tau * 6;
    s.buffer = noise(ac, 'white'); s.loop = true;
    bp.type = 'bandpass'; bp.frequency.value = e.long ? 5200 : 6100; bp.Q.value = 0.8;
    hp.type = 'highpass'; hp.frequency.value = 2400; hp.Q.value = 0.5;
    g.gain.setValueAtTime(0, when); g.gain.linearRampToValueAtTime(e.vel * SHAKE_GAIN, when + atk); g.gain.setTargetAtTime(0, when + atk, tau);
    s.connect(bp); bp.connect(hp); hp.connect(g); g.connect(busOf('shaker').in);
    s.start(when, hash(Math.round(when * 1000), 385) * 2.5); s.stop(end);
    registry.add({ src: [s], all: [s, bp, hp, g], start: when, end, tag: tagOf('shaker'), out: g });
  }
  /** the pad's chord from `when`, held until it is let go (registry.stop): per note a triangle and one a few cents
   *  over it (a single one in lite), all through a low-pass that leaves the body; it swells in (τ 0.14 s) */
  function hold(when, midis) {
    const nodes = [], src = [], N = n => (nodes.push(n), n);
    const lp = N(ac.createBiquadFilter()), amp = N(ac.createGain());
    lp.type = 'lowpass'; lp.frequency.value = 1150; lp.Q.value = 0.5;
    amp.gain.setValueAtTime(0, when); amp.gain.setTargetAtTime(1, when, 0.14);
    lp.connect(amp); amp.connect(busOf('pad').in);
    midis.forEach((m, k) => {
      const g = N(ac.createGain());
      g.gain.value = (k ? PAD_GAIN * 0.85 : PAD_GAIN) / Math.sqrt(midis.length / 3);
      for (const cents of lite ? [0] : [-4, 5]) {
        const o = N(ac.createOscillator());
        o.type = 'triangle'; o.frequency.value = mtof(m); o.detune.value = cents;
        o.connect(g); src.push(o); o.start(when);
      }
      g.connect(lp);
    });
    return registry.add({ src, all: nodes, start: when, end: Infinity, tag: tagOf('pad'), out: amp });
  }
  const letGo = (L, when, tau = 0.16) => { if (L.held) registry.stop(L.held, when, tau); Object.assign(L, { held: null, key: null, ends: null }); };

  /** what a part plays on 16th step `s` of the track, whose place on the grid is ac time `at` */
  const PLAY = {
    shaker(L, engine, s, at) {
      for (const e of L.events) if (e.s === s % 16) { shake(at + e.dt, e); note('shaker', s, at + e.dt); }
    },
    pad(L, engine, s, at) {
      const h = engine.harmony(at + 1e-4), midis = padNotes(h.tones, h.root), key = String(midis);
      if (key === L.key && L.held && !(L.ends !== null && s >= L.ends)) return;   // the same notes: held on (but a chord let go on this line is over)
      letGo(L, at);
      L.held = hold(at, midis); L.key = key;
      note('pad', s, at, { midis });
    },
    twinkle(L, engine, s, at) {
      for (const e of L.events) {
        if (e.s !== s % 16) continue;
        const when = at + e.dt, h = engine.harmony(when + 1e-4), style = h.plan.vibe.style;
        const notes = jamNotes(h.tones, h.root, h.key, h.plan, jamLow(style)), midi = notes[Math.min(e.i, notes.length - 1)];
        twinkle ??= createJam(ac, { musicIn: busOf('twinkle').in, revIn: mixer.revIn }, { registry, lite, tag: tagOf('twinkle'), level: 1, send: 0 });
        if (twinkle.note(when, midi, style, e.i)) note('twinkle', s, when, { midi, tones: h.tones, root: h.root });
      }
    },
  };
  const BARS = { shaker: shakerBar, twinkle: twinkleBar, pad: () => [] };

  const gridOf = engine => { const plan = engine && engine.plan; return plan && engine.startTime !== null ? { plan, start: engine.startTime, sd: 15 / plan.bpm, steps: plan.bars * 16 } : null; };
  /** the next bar line of the track playing that a switch can still take (never before its first), or null */
  const lineAfter = (g, at) => { const b = Math.max(0, barAfter(g.start, g.plan.bpm, at + LAYER_AHEAD)); return b < g.plan.bars ? b : null; };
  /** stop what a part has scheduled from ac time `when` on, and let what rings then go */
  const cut = (id, when, tau = 0.2) => { registry.stopTag(tagOf(id), when, tau); Object.assign(S[id], { held: null, key: null, ends: null }); };

  /** another track is playing: its own grid, from its first step; a switch still waiting takes its next bar line */
  function follow(engine, g, now) {
    if (engine.trackNo !== trackNo) {
      const skipped = trackNo !== null && now < trackEnd - 0.05;   // the old one was cut short (a skip, the radio tuning in)
      trackNo = engine.trackNo;
      for (const id of LAYER_IDS) {
        const L = S[id];
        if (skipped) cut(id, now, 0.1);
        else if (L.held) letGo(L, now, 0.3);
        Object.assign(L, { next: 0, barNo: -1, bar: null });
      }
    }
    trackEnd = g.start + g.steps * g.sd;
  }

  return {
    set(id, on, engine, at = ac.currentTime) {
      const L = S[id], g = gridOf(engine);
      if (!L) return 'same';
      if (g) follow(engine, g, at);
      const was = L.bar, reached = L.next, did = askLayer(L, on, g ? lineAfter(g, at) : null);
      const from = did === 'back' ? was : L.bar;        // the bar line the change is about
      if (did === 'same' || !g || from === null || L.want) return did;
      // off, or off after all: what is already scheduled past that bar line never starts, and what rings then (the
      // pad's chord) is let go on it
      const step = from * 16;
      registry.stopTag(tagOf(id), Math.max(g.start + step * g.sd, at), 0.2);   // (never from before now: a line just passed)
      if (reached > step) Object.assign(L, { held: null, key: null, ends: null });   // (its chord from past the line went with them)
      else if (L.held) L.ends = step;
      return did;
    },
    advance(until, engine, now = ac.currentTime, lag = 0) {
      const g = gridOf(engine);
      if (!g) return false;
      let flipped = false;
      follow(engine, g, now);
      const first = Math.max(0, Math.ceil((now + LAYER_AHEAD - g.start) / g.sd - 1e-9));
      for (const id of LAYER_IDS) {
        const L = S[id];
        if (L.want !== L.on) {
          if (L.bar === null) { L.bar = lineAfter(g, now); if (L.bar !== null) L.next = Math.min(L.next, L.bar * 16); }
          if (L.bar !== null && now - lag >= g.start + L.bar * 16 * g.sd) { L.on = L.want; L.bar = null; flipped = true; }
        }
        if (!L.want && !L.on) { L.next = Math.max(L.next, first); continue; }
        const fits = layerFits(id, g.plan);
        let s = Math.max(L.next, first);
        for (; s < g.steps && g.start + s * g.sd < until; s++) {
          const at = g.start + s * g.sd, bar = Math.floor(s / 16);
          if (bar !== L.barNo) {                        // a new bar: its figure, and whether the track is on its last held chord
            L.barNo = bar;
            L.last = !!engine.harmony(g.start + bar * 16 * g.sd + 1e-4).chord.final;
            L.events = fits && !L.last ? BARS[id](g.plan, bar) : [];
          }
          if (!soundsAt(L, s) || !fits) { if (L.held) letGo(L, at, 0.2); continue; }
          PLAY[id](L, engine, s, at);
        }
        if (s >= g.steps && L.held) letGo(L, trackEnd, 0.5);   // the track's last chord: the pad lets go with it
        L.next = s;
      }
      return flipped;
    },
    clear(at = ac.currentTime) {
      for (const id of LAYER_IDS) { Object.assign(S[id], { want: false, on: false, bar: null }); cut(id, at, 0.1); }
    },
    states(engine) {
      const g = gridOf(engine);
      return Object.fromEntries(LAYER_IDS.map(id => {
        const L = S[id];
        return [id, { want: L.want, on: L.on, fits: !g || layerFits(id, g.plan), at: g && L.want !== L.on && L.bar !== null ? g.start + L.bar * 16 * g.sd : null }];
      }));
    },
    stats: () => ({
      played: { ...played }, voices: Object.fromEntries(LAYER_IDS.map(id => [id, registry.count(tagOf(id))])),
      buses: Object.keys(buses), log: log.slice(),
    }),
    dispose() {
      for (const id of LAYER_IDS) { registry.stopTag(tagOf(id), ac.currentTime, 0.02); if (buses[id]) registry.free(buses[id].v); }
      if (twinkle) twinkle.dispose();
    },
  };
}
